import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNotifications } from './NotificationContext';
import NotificationItem from './NotificationItem';
import PushEnableBanner from './PushEnableBanner';
import {
  AppNotification,
  NotificationType,
  getActorProfileUrl,
  isInternalDeepLink,
  parseChallengeMessage,
} from './types';
import styles from './notifications.module.scss';
import { useNavigate } from 'react-router-dom';

interface NotificationPanelProps {
  onClose: () => void;
  /** "up" flips the panel above its trigger (bells docked in a bottom bar). */
  placement?: "down" | "up";
}

/** How many notifications to show before the "Show more" reveal. */
const COLLAPSED_COUNT = 5;

/** Anything created within this window is considered "New". */
const NEW_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h

/** Share of a row that must be on screen for it to count as seen. */
const SEEN_VISIBILITY = 0.6;
/** Seen rows are batched and flushed after this quiet period. */
const SEEN_FLUSH_MS = 400;

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose, placement = 'down' }) => {
  const { notifications, unreadCount, loading, markRead, markAllRead, markSeen } = useNotifications();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);

  // Instagram-style: a row is read once you've scrolled it into view, and it
  // keeps its unread marker until the panel closes so you can still tell
  // what was new. `shownUnread` remembers which rows arrived unread.
  const shownUnreadRef = useRef<Set<number>>(new Set());
  for (const n of notifications) {
    if (!n.read) shownUnreadRef.current.add(n.id);
  }
  const markSeenRef = useRef(markSeen);
  markSeenRef.current = markSeen;

  // Close on outside click — but let the click reach its real target
  // (so e.g. clicking the profile icon while the panel is open still toggles
  // the profile menu in the same click).
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target || !panelRef.current) return;
      // Don't close if click landed inside the panel itself OR inside the bell
      // wrapper (the bell handles its own toggle).
      if (panelRef.current.contains(target)) return;
      const bellWrapper = (target as HTMLElement).closest?.(`.${styles.bellWrapper}`);
      if (bellWrapper) return;
      onClose();
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.read) {
      await markRead(notification.id);
    }

    // Delivery-system notifications carry their own internal route — it
    // wins over the type-based mapping below. Validated server-side and
    // re-checked here so a bad row can never navigate off-site.
    if (isInternalDeepLink(notification.deep_link)) {
      navigate(notification.deep_link);
      onClose();
      return;
    }

    switch (notification.type) {
      case NotificationType.FOLLOW: {
        const profileUrl = getActorProfileUrl(notification.actor);
        if (profileUrl) navigate(profileUrl);
        break;
      }
      case NotificationType.COIN_TRANSFER:
        navigate('/coins');
        break;
      case NotificationType.CHALLENGE_JOIN:
      case NotificationType.CHALLENGE_RESULT: {
        const parsed = parseChallengeMessage(notification.type, notification.message);
        if (parsed && parsed.inviteCode) {
          navigate(`/march-madness-2026?join=${parsed.inviteCode}`);
        } else {
          navigate('/march-madness-2026');
        }
        break;
      }
      case NotificationType.LIKE:
      case NotificationType.REPLY:
      case NotificationType.MENTION:
      case NotificationType.REPOST:
      case NotificationType.QUOTE:
        navigate(notification.post_id ? `/feed/post/${notification.post_id}` : '/feed');
        break;
      case NotificationType.COMMUNITY_JOIN_REQUEST:
        navigate(
          notification.community_id
            ? `/communities/${notification.community_id}/members?tab=requests`
            : '/communities'
        );
        break;
      case NotificationType.COMMUNITY_JOIN_APPROVED:
        navigate(
          notification.community_id ? `/communities/${notification.community_id}` : '/communities'
        );
        break;
      default:
        break;
    }

    onClose();
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  // Split into "New" (last 24h) and "Earlier" (older). Keeps the existing
  // server ordering inside each group. Subtle, scannable hierarchy.
  const { newItems, earlierItems } = useMemo(() => {
    const now = Date.now();
    const newer: AppNotification[] = [];
    const older: AppNotification[] = [];
    for (const n of notifications) {
      const t = new Date(n.created_at).getTime();
      if (Number.isFinite(t) && now - t < NEW_THRESHOLD_MS) {
        newer.push(n);
      } else {
        older.push(n);
      }
    }
    return { newItems: newer, earlierItems: older };
  }, [notifications]);

  const totalCount = notifications.length;
  const canCollapse = totalCount > COLLAPSED_COUNT;
  const visibleNotifications = expanded || !canCollapse
    ? notifications
    : notifications.slice(0, COLLAPSED_COUNT);

  // Re-split the visible slice into the same groups (keeps order, hides
  // anything that got trimmed by the collapse limit).
  const visibleSet = useMemo(
    () => new Set(visibleNotifications.map((n) => n.id)),
    [visibleNotifications],
  );
  const visibleNew = newItems.filter((n) => visibleSet.has(n.id));
  const visibleEarlier = earlierItems.filter((n) => visibleSet.has(n.id));

  const remaining = Math.max(0, totalCount - COLLAPSED_COUNT);

  const unreadVisibleKey = visibleNotifications
    .filter((n) => !n.read)
    .map((n) => n.id)
    .join(',');

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !unreadVisibleKey || typeof IntersectionObserver === 'undefined') return;

    const pending = new Set<number>();
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      flushTimer = null;
      if (pending.size === 0) return;
      const ids = Array.from(pending);
      pending.clear();
      void markSeenRef.current(ids);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < SEEN_VISIBILITY) continue;
          const id = Number((entry.target as HTMLElement).dataset.notifId);
          if (Number.isFinite(id)) pending.add(id);
          observer.unobserve(entry.target);
        }
        if (pending.size > 0 && !flushTimer) flushTimer = setTimeout(flush, SEEN_FLUSH_MS);
      },
      { root: body, threshold: [SEEN_VISIBILITY] },
    );

    body
      .querySelectorAll<HTMLElement>('[data-notif-unread="true"]')
      .forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      if (flushTimer) clearTimeout(flushTimer);
      // Closing the panel mid-batch still counts what was on screen.
      flush();
    };
  }, [unreadVisibleKey]);

  const isInitialLoading = loading && notifications.length === 0;

  return (
    <div
      className={`${styles.panel}${placement === 'up' ? ` ${styles.panelUp}` : ''}`}
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
    >
      <span className={styles.panelGlass} aria-hidden="true" />
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleRow}>
          <h3 className={styles.panelTitle}>Notifications</h3>
          {unreadCount > 0 && (
            <span className={styles.unreadPill} aria-label={`${unreadCount} unread`}>
              {unreadCount > 99 ? '99+' : unreadCount} new
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button className={styles.markAllBtn} onClick={handleMarkAllRead}>
            Mark all read
          </button>
        )}
      </div>

      <PushEnableBanner />

      <div
        className={styles.panelBody}
        ref={bodyRef}
        onWheel={(e) => e.stopPropagation()}
      >
        {isInitialLoading ? (
          <div className={styles.skeletonList} aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className={styles.skeletonRow}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonTextBlock}>
                  <div className={styles.skeletonLine} />
                  <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </span>
            <span>You're all caught up</span>
            <span className={styles.emptySub}>New activity will show up here.</span>
          </div>
        ) : (
          <>
            {visibleNew.length > 0 && (
              <>
                <div className={styles.groupLabel}>New</div>
                {visibleNew.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notification={notif}
                    highlight={shownUnreadRef.current.has(notif.id)}
                    onClick={handleNotificationClick}
                    onClose={onClose}
                  />
                ))}
              </>
            )}
            {visibleEarlier.length > 0 && (
              <>
                <div className={styles.groupLabel}>Earlier</div>
                {visibleEarlier.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notification={notif}
                    highlight={shownUnreadRef.current.has(notif.id)}
                    onClick={handleNotificationClick}
                    onClose={onClose}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {!isInitialLoading && canCollapse && (
        <div className={styles.panelFooter}>
          <button
            className={styles.showMoreBtn}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Show less' : `Show ${remaining} more`}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
